<template>
  <div class="non-wear-detection-chart relative">
    <div v-if="showControls" class="mb-2 flex items-center relative">
      <!-- Centered Title -->
      <div v-if="headerTitle" class="absolute left-1/2 transform -translate-x-1/2">
        <h2 class="text-lg font-semibold text-gray-900">{{ headerTitle }}</h2>
      </div>
      
      <!-- Download Button on far right -->
      <div class="ml-auto">
        <ChartDownloadButton
          :chart-instance="chartInstance"
          chart-name="non-wear-detection-chart"
          :is-chart-ready="isChartReady"
        />
      </div>
    </div>
    <div class="relative">
      <BaseChart
        ref="baseChart"
        chart-type="line"
        :chart-data="processedChartData"
        :chart-options="chartOptions"
        :width="width"
        :height="height"
        :auto-update="autoUpdate"
        class="transition-opacity duration-300"
        :class="{ 'opacity-50': !isDataFullyLoaded }"
        @chart-ready="onChartReady"
        @chart-updated="onChartUpdated"
        @chart-error="onChartError"
      />
      <!-- Loading overlay -->
      <div
        v-if="!isDataFullyLoaded"
        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10"
      >
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p class="text-sm text-gray-600">Loading data<span class="loading-dots"></span></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseChart from './BaseChart.vue'
import ChartDownloadButton from './ChartDownloadButton.vue'
import { chartTooltipsMixin } from './mixins/chartTooltips.js'

export default {
  name: 'NonWearDetectionChart',
  components: {
    BaseChart,
    ChartDownloadButton
  },
  mixins: [chartTooltipsMixin],
  props: {
    // Data props
    filteredData: {
      type: Array,
      required: true
    },
    curveFeatures: {
      type: Array,
      default: () => []
    },
    selectedCurveIndex: {
      type: String,
      default: ''
    },
    // Header / controls
    headerTitle: {
      type: String,
      default: 'Non-Wear Detection'
    },
    showControls: {
      type: Boolean,
      default: false
    },
    // Chart display props
    width: {
      type: [Number, String],
      default: 800
    },
    height: {
      type: [Number, String],
      default: 400
    },
    autoUpdate: {
      type: Boolean,
      default: true
    },
    // Timeline props
    timelineStart: {
      type: String,
      required: true
    },
    timelineEnd: {
      type: String,
      required: true
    },

    // Curve timeline mapping for timezone-aware curve positioning
    curveTimelineMap: {
      type: Object,
      default: () => ({})
    },
    // Allow charts to render even when data has missing rows/gaps
    // Useful for day-level charts where some days may have incomplete data
    allowMissingRows: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartInstance: null,
      isChartReady: false,
      _cachedChartOptions: null,
      _cachedProcessedData: null
    }
  },
  computed: {
    // Check if data covers the entire x-axis range
    isDataFullyLoaded() {
      // If allowMissingRows is true, only check if we have any data at all
      // This allows charts to render even with gaps or incomplete coverage
      if (this.allowMissingRows) {
        return this.filteredData && this.filteredData.length > 0
      }
      
      // If no timeline bounds are set, consider data as loaded (can't verify)
      if (!this.timelineStart || !this.timelineEnd) {
        return true
      }
      
      // If no data, consider as not loaded
      if (!this.filteredData || this.filteredData.length === 0) {
        return false
      }
      
      try {
        const timelineStartTime = new Date(this.timelineStart).getTime()
        const timelineEndTime = new Date(this.timelineEnd).getTime()
        
        // Get all timestamps from the data
        const timestamps = this.filteredData
          .map(row => {
            if (row.tMs != null) {
              return row.tMs
            } else if (row.datetime) {
              const d = new Date(row.datetime)
              return isNaN(d.getTime()) ? null : d.getTime()
            }
            return null
          })
          .filter(ts => ts !== null)
          .sort((a, b) => a - b)
        
        if (timestamps.length === 0) {
          return false
        }
        
        const firstTimestamp = timestamps[0]
        const lastTimestamp = timestamps[timestamps.length - 1]
        
        // Allow a small tolerance (5 minutes) for edge cases
        const tolerance = 5 * 60 * 1000 // 5 minutes in milliseconds
        
        // Check if data covers the timeline range
        const coversStart = firstTimestamp <= timelineStartTime + tolerance
        const coversEnd = lastTimestamp >= timelineEndTime - tolerance
        
        // Also check for large gaps in the data (more than 2 hours gap might indicate missing data)
        let hasLargeGaps = false
        if (timestamps.length > 1) {
          for (let i = 1; i < timestamps.length; i++) {
            const gap = timestamps[i] - timestamps[i - 1]
            // If gap is larger than 2 hours, might indicate missing data
            if (gap > 2 * 60 * 60 * 1000) {
              hasLargeGaps = true
              break
            }
          }
        }
        
        // Data is fully loaded if it covers the range and doesn't have suspiciously large gaps
        const isLoaded = coversStart && coversEnd && !hasLargeGaps
        
        return isLoaded
      } catch (error) {
        console.warn('Error checking if data is fully loaded:', error)
        // On error, assume data is loaded to avoid blocking the UI
        return true
      }
    },
    
    // Process chart data for non-wear detection visualization
    processedChartData() {
      // Cache the processed data to prevent unnecessary recreation
      const cacheKey = `${this.filteredData?.length || 0}-${this.selectedCurveIndex}`
      if (this._cachedProcessedData && this._cachedProcessedData.key === cacheKey) {
        return this._cachedProcessedData.data
      }
      
      if (!this.filteredData || this.filteredData.length === 0) {

        return { datasets: [] }
      }



      
      const datasets = []
      
      // 1. Device worn (temperature)
      // Use type-coercing comparison to handle both numeric and string values
      const deviceOn = this.filteredData.filter(row => Number(row.device_worn_model) === 1)
      if (deviceOn.length > 0) {
        const deviceOnData = this.processDataPoints(deviceOn, 'Temperature_C')
        if (deviceOnData.length > 0) {
          datasets.push({
            label: 'Temperature (Wear)',
            data: deviceOnData,
            backgroundColor: 'darkblue',
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: false,
            type: 'scatter'
          })
        } else {
          console.log('⚠️ No valid deviceOn data points created')
        }
      }
      
      // 2. Device removed (temperature)
      // Use type-coercing comparison to handle both numeric and string values
      const deviceOff = this.filteredData.filter(row => Number(row.device_worn_model) === 0)
      if (deviceOff.length > 0) {
        const deviceOffData = this.processDataPoints(deviceOff, 'Temperature_C')
        if (deviceOffData.length > 0) {
          datasets.push({
            label: 'Temperature (Non-Wear)',
            data: deviceOffData,
            backgroundColor: 'red',
            pointStyle: 'x',
            pointRadius: 4,
            pointHoverRadius: 6,
            showLine: false,
            type: 'scatter'
          })
        } else {
          console.log('⚠️ No valid deviceOff data points created')
        }
      }
      
      // 3. Motion data if available
      if (this.filteredData[0]?.Motion !== undefined) {
        const motionData = this.processDataPoints(this.filteredData, 'Motion')
        if (motionData.length > 0) {
          datasets.push({
            label: 'Motion (G)',
            data: motionData,
            borderColor: 'gray',
            backgroundColor: 'rgba(128, 128, 128, 0.1)',
            borderWidth: 1,
            fill: false,
            yAxisID: 'motion',
            pointRadius: 0,
            type: 'line'
          })
        }
      }
      
      // 4. Temperature cutoff line removed per requirements
      
      // 5. Add curve start/end lines
      if (this.curveFeatures.length > 0) {
        this.addCurveLines(datasets)
      }
      
      // Filter out empty datasets
      const validDatasets = datasets.filter(dataset => dataset.data && dataset.data.length > 0)
      

      
      const result = { datasets: validDatasets }
      
      // Cache the processed data
      this._cachedProcessedData = {
        key: cacheKey,
        data: result
      }
      
      return result
    },
    
    // Chart options with timeline-specific configuration
    chartOptions() {
      // Don't create options if timeline is not ready
      if (!this.timelineStart || !this.timelineEnd) {

        return null
      }
      
      // Cache the options to prevent unnecessary recreation
      const cacheKey = `${this.timelineStart}-${this.timelineEnd}`
      if (this._cachedChartOptions && this._cachedChartOptions.key === cacheKey) {
        return this._cachedChartOptions.options
      }
      

      
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        plugins: {
          // Title handled externally via header; disable Chart.js title
          title: {
            display: false
          },
          tooltip: {
            enabled: false
          },
          legend: {
            display: true,
            position: 'top',
            onClick: (e, legendItem, legend) => {
              try {
                // Add defensive programming to prevent undefined errors
                if (!legendItem || !legend || !legend.chart) {
                  console.warn('Legend click handler: Invalid parameters received')
                  return
                }
                
                const chart = legend.chart;
                const index = legendItem.datasetIndex;
                
                if (typeof index === 'number' && index >= 0) {
                  const ci = chart.getDatasetMeta(index);
                  if (ci) {
                    if (ci.hidden) {
                      ci.hidden = false;
                    } else {
                      ci.hidden = true;
                    }
                    chart.update();
                  }
                }
              } catch (error) {
                console.warn('Error in legend onClick handler:', error)
              }
            },
            labels: {
              usePointStyle: true,
              padding: 20,
              generateLabels: (chart) => {
                const datasets = chart.data.datasets;
                const labels = [];
                
                datasets.forEach((dataset, index) => {
                  const meta = chart.getDatasetMeta(index);
                  if (meta.hidden === undefined) meta.hidden = false;
                  
                  // Create label with proper point style
                  const label = {
                    text: dataset.label,
                    fillStyle: dataset.backgroundColor || dataset.borderColor,
                    strokeStyle: dataset.borderColor,
                    lineWidth: dataset.borderWidth || 0,
                    hidden: meta.hidden,
                    datasetIndex: index
                  };
                  
                  // Set point style based on dataset
                  if (dataset.pointStyle) {
                    if (typeof dataset.pointStyle === 'function') {
                      label.pointStyle = dataset.pointStyle();
                    } else {
                      label.pointStyle = dataset.pointStyle;
                    }
                  }
                  
                  // Special handling for curve lines (dashed lines)
                  if (dataset.label && (dataset.label.includes('Curve Start') || dataset.label.includes('Curve End'))) {
                    label.lineDash = [6, 3];
                    label.lineWidth = 2;
                    label.pointStyle = 'line';
                  }
                  
                  labels.push(label);
                });
                
                return labels;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              // Remove displayFormats since we're using custom ticks.callback
              // displayFormats: {
              //   hour: 'HH:mm'
              // },
              // Simple date parsing without timezone conversion
              parser: function(dateString) {
                return new Date(dateString)
              }
            },
            title: {
              display: true,
              text: 'Time'
            },
            min: this.timelineStart ? new Date(this.timelineStart).getTime() : undefined,
            max: this.timelineEnd ? new Date(this.timelineEnd).getTime() : undefined,
            ticks: {
              callback: (value, index, ticks) => {
                // Simple time formatting without timezone conversion
                const date = new Date(value)
                const formatted = date.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })
                return formatted
              }
            }
          },
          y: {
            title: {
              display: true,
              text: 'Temperature (°C)'
            },
            position: 'left',
            min: 15,
            max: 40
          },
          motion: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            title: {
              display: true,
              text: 'Motion (G)'
            }
          }
        }
      }
      
      // Cache the options
      this._cachedChartOptions = {
        key: cacheKey,
        options: options
      }
      
      return options
    }
  },
  methods: {
    // Process data points for chart datasets
    processDataPoints(data, valueField, constantValue = null) {
      const processed = data.map(row => {
        try {
          const xMs = row.tMs != null ? row.tMs : (row.datetime ? new Date(row.datetime).getTime() : NaN)
          if (isNaN(xMs)) {
            console.warn('⚠️ Invalid timestamp:', row.tMs, row.datetime)
            return null
          }
          
          let y
          if (constantValue !== null) {
            y = constantValue
          } else if (valueField) {
            const rawValue = row[valueField]
            y = parseFloat(rawValue)
            if (isNaN(y)) {
              console.warn('⚠️ Invalid value in data:', {
                field: valueField,
                rawValue: rawValue,
                rowKeys: Object.keys(row)
              })
              return null
            }
          } else {
            return null
          }
          
          return { x: xMs, y }
        } catch (error) {
          console.error('Error processing data row:', error, row)
          return null
        }
      }).filter(item => item !== null)
      
      return processed
    },
    
    // Add curve start/end lines to datasets
    addCurveLines(datasets) {
      try {
        // Use full y-axis range to draw vertical lines from bottom to top
        const yStart = 15
        const yEnd = 40
        
        // Use curveTimelineMap if available, otherwise fallback to curveFeatures
        const curvesToProcess = Object.keys(this.curveTimelineMap).length > 0 ? 
          Object.values(this.curveTimelineMap) : 
          this.curveFeatures.map((curve, index) => ({
            id: curve.curve_id || index,
            index: index,
            startDate: new Date(curve.begin_CURVE),
            endDate: new Date(curve.end_CURVE)
          }))
        
        // Only process the first curve (index 0)
        if (curvesToProcess.length > 0) {
          const firstCurve = curvesToProcess[0]
          
          if (firstCurve.startDate) {
            const startTime = firstCurve.startDate
            const curveStartTimeForLine = startTime.getTime()
            
            datasets.push({
              label: 'Curve Start',
              data: [
                { x: curveStartTimeForLine, y: yStart },
                { x: curveStartTimeForLine, y: yEnd }
              ],
              borderColor: 'rgba(0, 0, 139, 0.9)',
              borderDash: [6, 3],
              borderWidth: 2,
              fill: false,
              pointRadius: 0,
              showLine: true,
              type: 'line'
            })
          }
          
          if (firstCurve.endDate) {
            const endTime = firstCurve.endDate
            const curveEndTimeForLine = endTime.getTime()
            
            datasets.push({
              label: 'Curve End',
              data: [
                { x: curveEndTimeForLine, y: yStart },
                { x: curveEndTimeForLine, y: yEnd }
              ],
              borderColor: 'rgba(139, 0, 0, 0.9)',
              borderDash: [6, 3],
              borderWidth: 2,
              fill: false,
              pointRadius: 0,
              showLine: true,
              type: 'line'
            })
          }
        }
      } catch (error) {
        console.error('Error creating curve datasets:', error)
      }
    },
    
    // Chart event handlers
    onChartReady(chart) {
      this.chartInstance = chart
      this.isChartReady = true
      
      // Setup tooltips after chart is ready
      this.$nextTick(() => {
        this.setupNonWearDetectionTooltips(chart)
      })
      
      this.$emit('chart-ready', chart)
    },
    
    onChartUpdated(chart) {
      this.$emit('chart-updated', chart)
    },
    
    onChartError(error) {
      console.error('Non-wear detection chart error:', error)
      this.$emit('chart-error', error)
    },
    
    // Clear caches when needed
    clearCaches() {
      this._cachedChartOptions = null
      this._cachedProcessedData = null
    },
    
    // Public methods for external access
    getChart() {
      return this.$refs.baseChart?.getChart()
    },
    
    isReady() {
      return this.$refs.baseChart?.isReady()
    },
    
    async updateChart(newData = null, newOptions = null) {
      // If no new data provided, use the current processed chart data
      if (!newData) {
        newData = this.processedChartData
        console.log('📊 NonWearDetectionChart: Using current processedChartData for update')
      }
      return await this.$refs.baseChart?.updateChart(newData, newOptions)
    },
    
    destroyChart() {
      return this.$refs.baseChart?.destroyChart()
    },
    
    forceStopChart() {
      return this.$refs.baseChart?.forceStopChart()
    },
    
    isChartHealthy() {
      return this.$refs.baseChart?.isChartHealthy()
    },
    
    clearErrorState() {
      return this.$refs.baseChart?.clearErrorState()
    },
    
    // Setup tooltips for non-wear detection chart
    setupNonWearDetectionTooltips(chart) {
      if (!chart || !this.curveFeatures?.length) return
      
      const getTooltipContent = (foundCurve, foundType, xValue, yValue) => {
        const curve = foundCurve.curve
        const curveIndex = foundCurve.index + 1
        const time = new Date(xValue).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        })
        
        if (foundType === 'start') {
          return `Curve ${curveIndex} Start\nTime: ${time}\nTemp: ${yValue?.toFixed(1) || 'N/A'}°C`
        } else {
          return `Curve ${curveIndex} End\nTime: ${time}\nTemp: ${yValue?.toFixed(1) || 'N/A'}°C`
        }
      }
      
      this.setupChartTooltips(chart, this.curveFeatures, getTooltipContent)
    }
  },
  watch: {
    // Watch for data changes to clear caches
    filteredData: {
      handler() {
        // Clear caches when data changes
        this.clearCaches()
      },
      deep: true
    },
    // Watch for timeline changes to clear caches
    timelineStart: {
      handler() {
        this.clearCaches()
      }
    },
    timelineEnd: {
      handler() {
        this.clearCaches()
      }
    },
    // Watch for curve selection changes to clear caches
    selectedCurveIndex: {
      handler() {
        this.clearCaches()
      }
    },
    // Watch for curve features changes to clear caches
    curveFeatures: {
      handler() {
        this.clearCaches()
      },
      deep: true
    }
  },
  mounted() {
    // Clear caches when component is mounted
    this.clearCaches()
  },
  beforeUnmount() {
    // Clear caches
    this.clearCaches()
    // Clean up tooltips
    this.cleanupTooltips()
  }
}
</script>

<style scoped>
.non-wear-detection-chart {
  width: 100%;
  height: 100%;
}

@keyframes dotAnimation {
  0%, 20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%, 100% {
    content: '...';
  }
}

.loading-dots::after {
  content: '';
  animation: dotAnimation 1.5s steps(4, end) infinite;
}
</style>

<style>
/* Global tooltip styles for chart components */
.chart-curve-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-family: Arial, sans-serif;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  white-space: pre-line;
  line-height: 1.4;
}
</style>
